/**
 * Main App Component
 * Updated with Bloomberg Terminal-inspired navigation system
 * Includes In-App Guidance System (Onboarding, Lets Talk)
 *
 * Performance Optimizations (Dec 27, 2025):
 * - Route-based code splitting with React.lazy()
 * - Suspense boundaries for async page loading
 * - Critical path pages loaded eagerly (Login, Dashboard)
 * - Heavy feature pages lazy-loaded (Legal, Portfolio, etc.)
 */

import { useState, useEffect, useLayoutEffect, Suspense, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { Loader2, Menu } from 'lucide-react';
// Agentation removed - was causing green bar UI issues
// import { Agentation } from 'agentation';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BackgroundJobsProvider } from './contexts/BackgroundJobsContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { PlatformAdminRoute } from './components/auth/PlatformAdminRoute';
import { trackFeatureFailure } from './lib/featureFailures';
import { init as initAnalytics, setAnalyticsPrivacyContext } from './lib/analytics';
import {
  hasAnalyticsConsent,
  recordAppLoaded,
  recordFeatureOpened,
  recordPageViewed,
} from './lib/analytics/productEvents';
import { cn } from './lib/utils';

// Critical path - loaded eagerly for fast initial render
import { Login } from './pages/Login';
import { ChatGPTConsent } from './pages/ChatGPTConsent';
import { getChatGPTConsentReturnTo } from './lib/chatgptConsent';
import { Signup } from './pages/Signup';
import { VerifyEmail } from './pages/VerifyEmail';
import { AuthAction } from './pages/AuthAction';
import { ForceMFA } from './pages/ForceMFA';
import { Dashboard } from './pages/Dashboard';
import { Settings } from './pages/Settings';
import LandingPage from './pages/LandingPage';
import CorbelLandingPage from './pages/CorbelLandingPage';
import { NotFound } from './pages/errors/NotFound';
import { useBrand } from './hooks/useBrand';
import { applyProtectedTheme, ThemeProvider, useTheme } from './contexts/ThemeContext';
import { lazyWithRetry } from './lib/lazyWithRetry';

// Heavy pages - lazy loaded for better performance
const ProjectDetail = lazyWithRetry(() => import('./pages/ProjectDetail').then(m => ({ default: m.ProjectDetail })));
const CIMAnalysisView = lazyWithRetry(() => import('./pages/CIMAnalysisView').then(m => ({ default: m.CIMAnalysisView })));
const CIMTeaserAnalysisView = lazyWithRetry(() => import('./pages/CIMTeaserAnalysisView').then(m => ({ default: m.CIMTeaserAnalysisView })));
const LegalHub = lazyWithRetry(() => import('./features/legal/LegalHub'));
const DocumentEditorPage = lazyWithRetry(() => import('./features/legal/DocumentEditorPage'));
const DocumentsManagement = lazyWithRetry(() => import('./features/legal/DocumentsManagement'));
const DocumentViewer = lazyWithRetry(() => import('./features/legal/DocumentViewer'));
const LegalGeneratorPage = lazyWithRetry(() => import('./pages/LegalGeneratorPage'));
const Portfolio = lazyWithRetry(() => import('./pages/Portfolio').then(m => ({ default: m.Portfolio })));
const Documents = lazyWithRetry(() => import('./pages/Documents').then(m => ({ default: m.Documents })));
const GraphicsStudioPage = lazyWithRetry(() => import('./features/graphics/GraphicsStudioPage'));
const ModelGenerator = lazyWithRetry(() => import('./pages/ModelGenerator').then(m => ({ default: m.ModelGenerator })));
const PortfolioMonitoringDashboard = lazyWithRetry(() => import('./features/portfolio-monitoring').then(m => ({ default: m.PortfolioMonitoringDashboard })));
const PortfolioCompanyPage = lazyWithRetry(() => import('./features/portfolio-company/pages/PortfolioCompanyPage').then(m => ({ default: m.PortfolioCompanyPage })));
const Admin = lazyWithRetry(() => import('./pages/Admin'));
const SharePointSyncPanel = lazyWithRetry(() => import('./features/sharepoint-sync/SharePointSyncPanel'));
const DealLifecyclePage = lazyWithRetry(() => import('./pages/DealLifecyclePage').then(m => ({ default: m.DealLifecyclePage })));
const Welcome = lazyWithRetry(() => import('./pages/Welcome'));
const ExecutiveBriefQaPage = import.meta.env.DEV
  ? lazyWithRetry(() => import('./features/ic-memo/components/ExecutiveBriefQaPage').then(m => ({ default: m.ExecutiveBriefQaPage })))
  : null;

// Marketing pages - lazy loaded, public (no auth required)
const MarketingLayout = lazyWithRetry(() => import('./components/marketing/MarketingLayout').then(m => ({ default: m.MarketingLayout })));
const FeaturesPage = lazyWithRetry(() => import('./pages/marketing/FeaturesPage'));
const PricingPage = lazyWithRetry(() => import('./pages/marketing/PricingPage'));
const UseCasesPage = lazyWithRetry(() => import('./pages/marketing/UseCasesPage'));
const UseCaseManagingPartners = lazyWithRetry(() => import('./pages/marketing/UseCaseManagingPartners'));
const UseCaseDealTeams = lazyWithRetry(() => import('./pages/marketing/UseCaseDealTeams'));
const UseCasePortfolioOps = lazyWithRetry(() => import('./pages/marketing/UseCasePortfolioOps'));
const SolutionPage = lazyWithRetry(() => import('./pages/marketing/SolutionPage'));
const ComparisonPage = lazyWithRetry(() => import('./pages/marketing/ComparisonPage'));
const ResourcePage = lazyWithRetry(() => import('./pages/marketing/ResourcePage'));
const AboutPage = lazyWithRetry(() => import('./pages/marketing/AboutPage'));
const BlogIndex = lazyWithRetry(() => import('./pages/marketing/BlogIndex'));
const BlogPost = lazyWithRetry(() => import('./pages/marketing/BlogPost'));
const DownloadPage = lazyWithRetry(() => import('./pages/marketing/DownloadPage'));
const TrustPage = lazyWithRetry(() => import('./pages/marketing/TrustPage'));
const PrivacyPage = lazyWithRetry(() => import('./pages/marketing/PrivacyPage'));
const TermsPage = lazyWithRetry(() => import('./pages/marketing/TermsPage'));

// Sentry-wrapped Routes for automatic route-change transactions
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

// Navigation Components
import { AppSidebar } from './components/navigation/AppSidebar';
import { Breadcrumbs } from './components/navigation/Breadcrumbs';
import { BottomNav } from './components/navigation/BottomNav';
import { ProjectSwitcher } from './components/navigation/ProjectSwitcher';
import { NotificationBell } from './components/notifications/NotificationBell';
import { AvatarPromptDialog } from './components/profile/AvatarPromptDialog';
import { UserAvatar } from './components/shared/UserAvatar';
// VoiceAgentWidgetEnhanced moved to ProjectDetail Voice tab only - removed from global layout to prevent duplicate instances

// Guidance System Components
import { LetsTalkWidget } from './components/guidance';
import { OnboardingBanner } from './features/onboarding/components/OnboardingBanner';
import { useOnboardingGate } from './features/onboarding/hooks/useOnboardingGate';

/**
 * Page-level loading spinner for route transitions
 * Provides visual feedback during lazy component loading
 */
const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center h-full min-h-[400px] bg-background">
    <div className="flex flex-col items-center gap-4">
      <Loader2 className="h-10 w-10 animate-spin text-primary" />
      <p className="text-sm text-muted-foreground animate-pulse">Loading...</p>
    </div>
  </div>
);

// Create a client with optimized caching
const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      const meta = (mutation.options.meta || {}) as Record<string, unknown>;
      const mutationKey = mutation.options.mutationKey
        ? JSON.stringify(mutation.options.mutationKey)
        : undefined;

      void trackFeatureFailure(error, {
        feature: typeof meta.feature === 'string' ? meta.feature : 'react-query',
        operation: typeof meta.operation === 'string' ? meta.operation : 'mutation',
        severity: typeof meta.severity === 'string' && ['low', 'medium', 'high', 'critical'].includes(meta.severity)
          ? meta.severity as 'low' | 'medium' | 'high' | 'critical'
          : 'medium',
        userVisible: meta.userVisible !== false,
        retryable: meta.retryable === true,
        projectId: typeof meta.projectId === 'string' ? meta.projectId : undefined,
        organizationId: typeof meta.organizationId === 'string' ? meta.organizationId : undefined,
        metadata: {
          mutationKey,
        },
      });
    },
  }),
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      // Performance: Cache data for 5 minutes to reduce re-fetches
      staleTime: 5 * 60 * 1000,
      // Performance: Keep inactive queries in cache for 30 minutes
      gcTime: 30 * 60 * 1000,
    },
  },
});

/**
 * Layout wrapper for authenticated pages
 * Includes sidebar, breadcrumbs, main content area, and guidance system
 */
const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { user, userProfile } = useAuth();
  const { theme } = useTheme();
  const projectId = location.pathname.split('/')[2] || 'test-project';
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (window.innerWidth < 1024) {
      return true;
    }

    try {
      const stored = localStorage.getItem('corbel_sidebar_collapsed');
      return stored ? JSON.parse(stored) : true;
    } catch {
      return true;
    }
  });

  // Setup guide: sends a new user landing on the dashboard to /welcome, and shows the reminder banner elsewhere
  const onboardingGate = useOnboardingGate();
  const topbarAvatarUrl = userProfile?.avatarUrl || userProfile?.photoURL || user?.photoURL || null;
  const topbarDisplayName = userProfile?.displayName || user?.displayName || user?.email || 'User';

  const handleMobileMenuClick = () => {
    window.dispatchEvent(new Event('toggleSidebar'));
  };

  // Don't show layout on login/signup/landing/marketing pages
  const isAuthPage = location.pathname === '/login' || location.pathname === '/signup' || location.pathname === '/verify-email' || location.pathname === '/setup-mfa' || location.pathname.startsWith('/auth/action') || location.pathname === '/chatgpt/consent';
  const isLandingPage = location.pathname === '/';
  const marketingPaths = ['/features', '/pricing', '/use-cases', '/solutions', '/compare', '/resources', '/about', '/blog', '/download', '/trust', '/privacy', '/terms'];
  const isMarketingPage = marketingPaths.some(p =>
    location.pathname === p || location.pathname.startsWith(p + '/')
  );
  const protectedPaths = [
    '/dashboard',
    '/graphics',
    '/project/',
    '/projects/',
    '/legal',
    '/admin',
    '/settings',
    '/deal-lifecycle',
    '/portfolio',
    '/portfolio-monitor',
    '/documents',
    '/model-generator',
  ];
  const isProtectedAppPage = protectedPaths.some(p =>
    p.endsWith('/') ? location.pathname.startsWith(p) : location.pathname === p || location.pathname.startsWith(p + '/')
  );
  const isQaPage = import.meta.env.DEV && location.pathname.startsWith('/__qa/');
  // The setup guide is full-page (no sidebar, no Lets Talk launcher) but still the signed-in workspace, so it follows the user's theme.
  const isWelcomePage = location.pathname === '/welcome';

  useLayoutEffect(() => {
    applyProtectedTheme(theme, isProtectedAppPage || isWelcomePage);
    return () => applyProtectedTheme('light', false);
  }, [isProtectedAppPage, isWelcomePage, theme]);

  if (isAuthPage || isQaPage || isMarketingPage || (isLandingPage && !user) || !isProtectedAppPage) {
    return <>{children}</>;
  }

  return (
    <div className="quantum-app flex h-screen overflow-hidden bg-background text-foreground">
      {/* Skip to Main Content Link - WCAG 2.2 Accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:shadow-lg focus:ring-2 focus:ring-offset-2 focus:ring-primary"
      >
        Skip to main content
      </a>

      {/* Persistent Sidebar */}
      <AppSidebar collapsed={sidebarCollapsed} onCollapsedChange={setSidebarCollapsed} />

      {/* Main Content Area */}
      <main
        id="main-content"
        tabIndex={-1}
        className={cn(
          'flex-1 flex flex-col overflow-hidden ml-0 transition-[margin] duration-300 ease',
          sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
        )}
      >
        {/* Top Bar with Breadcrumbs and Project Switcher */}
        <div className="quantum-topbar h-14 lg:h-16 bg-background/95 border-b border-border flex items-center justify-between gap-2 px-3 sm:px-4 lg:px-6 flex-shrink-0">
          <div className="flex min-w-0 items-center gap-2">
            <button
              type="button"
              onClick={handleMobileMenuClick}
              className="inline-flex h-11 w-11 items-center justify-center border border-border bg-card text-foreground shadow-sm transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 lg:hidden"
              aria-label="Open main navigation"
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </button>
            {topbarAvatarUrl && (
              <Link
                to="/settings?tab=profile"
                className="inline-flex rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 lg:hidden"
                aria-label="Open profile settings"
              >
                <UserAvatar
                  src={topbarAvatarUrl}
                  name={topbarDisplayName}
                  email={user?.email}
                  className="h-9 w-9 shadow-sm"
                  fallbackClassName="text-xs"
                />
              </Link>
            )}
            <div className="hidden min-w-0 sm:block">
              <Breadcrumbs />
            </div>
          </div>

          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <NotificationBell />
            <div className="hidden sm:block">
              <ProjectSwitcher />
            </div>
          </div>
        </div>

        {onboardingGate.showBanner && <OnboardingBanner onDismiss={onboardingGate.dismissBanner} />}

        {/* Page Content */}
        <div className="quantum-page flex-1 overflow-y-auto pb-[calc(4rem_+_env(safe-area-inset-bottom))] lg:pb-0">
          <div className="quantum-grid-accent" aria-hidden="true" />
          {children}
        </div>
      </main>

      <BottomNav />

      <LetsTalkWidget />
      <AvatarPromptDialog />
    </div>
  );
};

/**
 * Helper component to redirect authenticated users away from public pages
 */
const AuthRedirect: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // DEBUG: Track AuthRedirect state
  console.log('[AuthRedirect] Rendering - loading:', loading, 'user:', user?.email || 'null');

  if (loading) {
    console.log('[AuthRedirect] Showing loading spinner (loading=true)');
    return <div className="flex items-center justify-center h-screen bg-background"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div></div>;
  }

  if (user) {
    const returnTo = getChatGPTConsentReturnTo(new URLSearchParams(location.search).get('returnTo'));
    console.log('[AuthRedirect] User authenticated, continuing after sign-in');
    return <Navigate to={returnTo} replace />;
  }

  console.log('[AuthRedirect] No user, showing children');
  return <>{children}</>;
};

/**
 * Renders the appropriate landing page based on the domain
 */
const DomainLandingPage: React.FC = () => {
  const { brand } = useBrand();
  return brand === 'returncatalyst' ? <LandingPage /> : <CorbelLandingPage />;
};

const AnalyticsBridge: React.FC = () => {
  const location = useLocation();
  const { user, userProfile, loading } = useAuth();
  const appLoadedTrackedRef = useRef(false);
  const lastTrackedRouteRef = useRef<string | null>(null);

  useEffect(() => {
    if (loading) return;

    const hasUserConsent = Boolean(user?.uid) && hasAnalyticsConsent(userProfile?.preferences);

    setAnalyticsPrivacyContext({
      hasConsent: hasUserConsent,
      distinctId: user?.uid,
    });

    if (!hasUserConsent) {
      appLoadedTrackedRef.current = false;
      lastTrackedRouteRef.current = null;
      return;
    }

    void initAnalytics().then((active) => {
      if (!active) return;

      if (!appLoadedTrackedRef.current) {
        recordAppLoaded(true);
        appLoadedTrackedRef.current = true;
      }

      const routeKey = `${location.pathname}?${location.search}`;
      if (lastTrackedRouteRef.current === routeKey) return;
      lastTrackedRouteRef.current = routeKey;

      recordPageViewed(location.pathname);
      recordFeatureOpened(location.pathname, location.search);
    });
  }, [loading, location.pathname, location.search, user?.uid, userProfile?.preferences]);

  return null;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <BackgroundJobsProvider>
            <Router>
              <AnalyticsBridge />
              <AppLayout>
                {/* Suspense boundary for lazy-loaded routes */}
                <Suspense fallback={<PageLoader />}>
                  <SentryRoutes>
                    {/* Public routes - eagerly loaded */}
                    {/* AuthRedirect ensures authenticated users go to dashboard instead of staying on login/signup */}
                    <Route path="/login" element={<AuthRedirect><Login /></AuthRedirect>} />
                    <Route path="/chatgpt/consent" element={<ChatGPTConsent />} />
                    <Route path="/signup" element={<AuthRedirect><Signup /></AuthRedirect>} />
                    <Route path="/verify-email" element={<VerifyEmail />} />
                    <Route path="/auth/action" element={<AuthAction />} />
                    <Route path="/setup-mfa" element={<ForceMFA />} />

                    {/* Protected routes - Dashboard eagerly loaded for fast access */}
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute>
                          <Dashboard />
                        </ProtectedRoute>
                      }
                    />
                    {/* Setup guide - full page, rendered outside the AppLayout chrome (see isWelcomePage) */}
                    <Route
                      path="/welcome"
                      element={
                        <ProtectedRoute>
                          <Welcome />
                        </ProtectedRoute>
                      }
                    />
                    {/* Heavy pages - lazy loaded for better initial bundle size */}
                    <Route
                      path="/project/:projectId"
                      element={
                        <ProtectedRoute>
                          <ProjectDetail />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/graphics"
                      element={
                        <ProtectedRoute>
                          <GraphicsStudioPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/projects/:projectId/cim/:analysisId"
                      element={
                        <ProtectedRoute>
                          <CIMAnalysisView />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/projects/:projectId/cim-teaser/:teaserId"
                      element={
                        <ProtectedRoute>
                          <CIMTeaserAnalysisView />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/legal"
                      element={
                        <ProtectedRoute>
                          <LegalHub />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/legal/create/:templateId"
                      element={
                        <ProtectedRoute>
                          <DocumentEditorPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/legal/create/:templateId/:projectId"
                      element={
                        <ProtectedRoute>
                          <DocumentEditorPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/legal/documents"
                      element={
                        <ProtectedRoute>
                          <DocumentsManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/legal/edit/:documentId"
                      element={
                        <ProtectedRoute>
                          <DocumentEditorPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/legal/view/:documentId"
                      element={
                        <ProtectedRoute>
                          <DocumentViewer />
                        </ProtectedRoute>
                      }
                    />
                    {/* NEW: Simplified Legal Document Generator with Gemini 3 Pro */}
                    <Route
                      path="/legal/generate"
                      element={
                        <ProtectedRoute>
                          <LegalGeneratorPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/legal/generate/:projectId"
                      element={
                        <ProtectedRoute>
                          <LegalGeneratorPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* Platform Admin - restricted to super-admins */}
                    <Route
                      path="/admin"
                      element={
                        <PlatformAdminRoute>
                          <Admin />
                        </PlatformAdminRoute>
                      }
                    />
                    <Route
                      path="/admin/sharepoint"
                      element={
                        <PlatformAdminRoute>
                          <Suspense fallback={<PageLoader />}>
                            <SharePointSyncPanel />
                          </Suspense>
                        </PlatformAdminRoute>
                      }
                    />
                    <Route
                      path="/settings"
                      element={
                        <ProtectedRoute>
                          <Settings />
                        </ProtectedRoute>
                      }
                    />

                    {/* Deal Lifecycle Board */}
                    <Route
                      path="/deal-lifecycle"
                      element={
                        <ProtectedRoute>
                          <DealLifecyclePage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Portfolio Management */}
                    <Route
                      path="/portfolio"
                      element={
                        <ProtectedRoute>
                          <Portfolio />
                        </ProtectedRoute>
                      }
                    />

                    {/* Portfolio Company Detail - Individual company tearsheet view */}
                    <Route
                      path="/portfolio/:companyId"
                      element={
                        <ProtectedRoute>
                          <PortfolioCompanyPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Portfolio Monitoring Dashboard - Enhanced real-time monitoring */}
                    <Route
                      path="/portfolio-monitor"
                      element={
                        <ProtectedRoute>
                          <PortfolioMonitoringDashboard />
                        </ProtectedRoute>
                      }
                    />

                    {/* Knowledge Base Repository */}
                    <Route
                      path="/documents"
                      element={
                        <ProtectedRoute>
                          <Documents />
                        </ProtectedRoute>
                      }
                    />

                    {/* AI Model Generator - standalone page with optional projectId */}
                    <Route
                      path="/model-generator"
                      element={
                        <ProtectedRoute>
                          <ModelGenerator />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/model-generator/:projectId"
                      element={
                        <ProtectedRoute>
                          <ModelGenerator />
                        </ProtectedRoute>
                      }
                    />

                    {/* Public marketing pages - NOT wrapped with AuthRedirect */}
                    <Route path="/features" element={<MarketingLayout><FeaturesPage /></MarketingLayout>} />
                    <Route path="/pricing" element={<MarketingLayout><PricingPage /></MarketingLayout>} />
                    <Route path="/use-cases" element={<MarketingLayout><UseCasesPage /></MarketingLayout>} />
                    <Route path="/use-cases/managing-partners" element={<MarketingLayout><UseCaseManagingPartners /></MarketingLayout>} />
                    <Route path="/use-cases/deal-teams" element={<MarketingLayout><UseCaseDealTeams /></MarketingLayout>} />
                    <Route path="/use-cases/portfolio-ops" element={<MarketingLayout><UseCasePortfolioOps /></MarketingLayout>} />
                    <Route path="/solutions/:slug" element={<MarketingLayout><SolutionPage /></MarketingLayout>} />
                    <Route path="/compare/:slug" element={<MarketingLayout><ComparisonPage /></MarketingLayout>} />
                    <Route path="/resources/:slug" element={<MarketingLayout><ResourcePage /></MarketingLayout>} />
                    <Route path="/about" element={<MarketingLayout><AboutPage /></MarketingLayout>} />
                    <Route path="/blog" element={<MarketingLayout><BlogIndex /></MarketingLayout>} />
                    <Route path="/blog/:slug" element={<MarketingLayout><BlogPost /></MarketingLayout>} />
                    <Route path="/download" element={<MarketingLayout><DownloadPage /></MarketingLayout>} />
                    <Route path="/trust" element={<MarketingLayout><TrustPage /></MarketingLayout>} />
                    <Route path="/privacy" element={<MarketingLayout><PrivacyPage /></MarketingLayout>} />
                    <Route path="/privacypolicy" element={<Navigate to="/privacy" replace />} />
                    <Route path="/terms" element={<MarketingLayout><TermsPage /></MarketingLayout>} />

                    {/* Landing Page with Auth Redirect - domain-aware */}
                    <Route
                      path="/"
                      element={
                        <AuthRedirect>
                          <DomainLandingPage />
                        </AuthRedirect>
                      }
                    />

                    {ExecutiveBriefQaPage && (
                      <Route path="/__qa/ic-memo-executive-summary" element={<ExecutiveBriefQaPage />} />
                    )}

                    {/* 404 */}
                    <Route path="*" element={<MarketingLayout><NotFound /></MarketingLayout>} />
                  </SentryRoutes>
                </Suspense>
              </AppLayout>
            </Router>

            {/* Toast Notifications */}
            <Toaster
              position="bottom-right"
              expand={false}
              richColors
              closeButton
              toastOptions={{
                className: 'toast-notification',
                duration: 4000,
                closeButton: true,
              }}
            />

            {/* Agentation removed - was causing green bar UI overlay issues */}
          </BackgroundJobsProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
